import React from 'react';
import { Filter, RotateCcw, X } from "lucide-react";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Combobox } from "../ui/combobox";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { cn } from "../ui/utils";

interface TicketFilterPopoverProps {
  serviceDesks: string[];
  assignees: string[];
  selectedServiceDesk: string;
  setSelectedServiceDesk: (desk: string) => void;
  selectedAssignee: string;
  setSelectedAssignee: (assignee: string) => void;
  hideDeskFilter?: boolean;
  className?: string;
}

export const TicketFilterPopover: React.FC<TicketFilterPopoverProps> = ({
  serviceDesks,
  assignees,
  selectedServiceDesk,
  setSelectedServiceDesk,
  selectedAssignee,
  setSelectedAssignee,
  hideDeskFilter = false,
  className
}) => {
  const resetFilters = () => {
    setSelectedServiceDesk("all");
    setSelectedAssignee("all");
  };

  const activeCount = (!hideDeskFilter && selectedServiceDesk !== "all" ? 1 : 0) + (selectedAssignee !== "all" ? 1 : 0);

  return (
    <Popover>
      <PopoverTrigger asChild className={className}>
        <Button 
          variant="outline" 
          size="sm" 
          className={cn(
            "h-9 px-4 shadow-sm relative transition-all",
            activeCount > 0 
              ? "border-black text-black bg-gray-50/50" 
              : "text-gray-700 border-gray-200",
            className
          )}
        >
          <Filter className={cn("mr-2 h-4 w-4", activeCount > 0 ? "text-black" : "text-gray-500")} /> 
          Filter
          {activeCount > 0 && (
            <span className="ml-2 flex items-center justify-center bg-black text-white text-[10px] font-black h-4 w-4 rounded-full">
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 shadow-xl border-gray-200 rounded-xl overflow-hidden" align="end" sideOffset={8}>
        <div className="bg-gray-50/50 px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h4 className="text-sm font-bold text-gray-900">Filters</h4>
          {activeCount > 0 && (
            <button 
              onClick={resetFilters}
              className="text-[10px] font-bold uppercase tracking-widest text-gray-900 hover:text-black transition-colors"
            >
              Clear All
            </button>
          )}
        </div>
        
        <div className="p-4 space-y-6">
          {/* Service Desk Section */}
          {!hideDeskFilter && (
            <div className="space-y-2">
              <Label className="text-xs font-normal text-black">
                Service Desk
              </Label>
              <Combobox
                options={[
                  { value: "all", label: "All Service Desks" },
                  ...serviceDesks.map((desk) => ({ value: desk, label: desk })),
                ]}
                value={selectedServiceDesk}
                onChange={(value) => setSelectedServiceDesk(value ?? "all")}
                placeholder="All Service Desks"
                searchPlaceholder="Search service desks..."
                className="w-full font-normal text-gray-700"
              />
            </div>
          )}

          {/* Assigned To Section */}
          <div className="space-y-2">
            <Label className="text-xs font-normal text-black">
              Assigned To
            </Label>
            <Combobox
              options={[
                { value: "all", label: "All Agents" },
                { value: "unassigned", label: "Unassigned Only" },
                ...assignees.map((name) => ({ value: name, label: name })),
              ]}
              value={selectedAssignee}
              onChange={(value) => setSelectedAssignee(value ?? "all")}
              placeholder="All Agents"
              searchPlaceholder="Search agents..."
              className="w-full font-normal text-gray-700"
            />
          </div>

          {/* Active Filters Section */}
          {activeCount > 0 && (
            <div className="space-y-3 pt-2">
              <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                Active filters
              </Label>
              <div className="flex flex-wrap gap-2">
                {selectedServiceDesk !== "all" && (
                  <Badge variant="secondary" className="bg-gray-50 text-gray-700 border-gray-100 h-7 pl-2 pr-1 gap-1 font-bold text-[10px] rounded-md">
                    Desk: {selectedServiceDesk}
                    <button onClick={() => setSelectedServiceDesk("all")} className="hover:bg-gray-200 rounded-sm p-0.5 transition-colors text-gray-400 hover:text-gray-600">
                      <X size={12} />
                    </button>
                  </Badge>
                )}
                {selectedAssignee !== "all" && (
                  <Badge variant="secondary" className="bg-gray-50 text-gray-700 border-gray-100 h-7 pl-2 pr-1 gap-1 font-bold text-[10px] rounded-md">
                    Agent: {selectedAssignee}
                    <button onClick={() => setSelectedAssignee("all")} className="hover:bg-gray-200 rounded-sm p-0.5 transition-colors text-gray-400 hover:text-gray-600">
                      <X size={12} />
                    </button>
                  </Badge>
                )}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
